/**
 * CevirilerPage.jsx — Sistem Ayarları › Çeviriler (backlog #337, 2026-10-13)
 * Route: /Management/SistemAyarlari/Ceviriler  (page.sistem; GET /ceviri/eksikler yalnızca personel)
 *
 * Veritabanındaki adların İngilizcesi ne kadar tamam: her çevrilen katalog için personelin gördüğü satır
 * sayısı ve İngilizcesi olmayanlar (boş İngilizce de eksik sayılır). İngilizcesi olmayan bir ad İngilizce
 * ekranlarda Türkçe görünür. Adlar burada değil, her kataloğun kendi sayfasında düzenlenir; kodla gelen
 * kataloglarınki (durumlar, talep türleri, roller) geliştiricilerin R__text_catalogs_en.sql dosyasında.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Table, Alert, Spinner, Progress, Badge, Button } from "reactstrap";
import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getEksikCeviriler } from "../../../services/veriAyarlariService";
import { currentLocaleTag } from "../../../i18n";

// katalog (tablo adı) → nerede düzenlenir: bir sayfa (link) ya da neden burada düzenlenmediği (not:
// ceviriler.notlar.<not>). Listede olmayan bir katalog "kodla gelir" sayılır.
const DUZENLEME = {
  file_type: { link: "/Management/SistemAyarlari/DosyaTurleri" },
  firma_type: { link: "/Management/SistemAyarlari/FirmaTurleri" },
  fuar: { link: "/Management/Fairs" },
  kullanim_kurali: { link: "/Management/Projects/GlobalRules?tab=kullanim" },
  meslek: { not: "meslek" },
  proje_beyani_alan: { not: "api" },
};

export default function CevirilerPage() {
  const { t, i18n } = useTranslation("ayarlar");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [yenile, setYenile] = useState(0);

  useEffect(() => { document.title = `${t("ceviriler.baslik")} | FDS`; }, [t, i18n.language]);

  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const data = await getEksikCeviriler();
        if (!ignore) { setRows(data); setError(null); }
      } catch (e) {
        if (!ignore) setError(e.message || t("ceviriler.okunamadi"));
      }
    }
    run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yenile]);

  const sayi = (n) => Number(n || 0).toLocaleString(currentLocaleTag());
  const toplamEksik = (rows || []).reduce((a, r) => a + Number(r.eksik || 0), 0);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("ceviriler.baslik")} pageTitle={t("ceviriler.ustBaslik")} />
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-1">{t("ceviriler.baslik")}</h5>
              <div className="text-muted small">{t("ceviriler.aciklama")}</div>
            </div>
            <Button color="light" size="sm" onClick={() => { setRows(null); setYenile((x) => x + 1); }}>
              <RefreshCw size={14} className="me-1" />{t("ceviriler.yenile")}
            </Button>
          </CardHeader>
          <CardBody>
            {error && <Alert color="danger">{error}</Alert>}
            {!rows && !error && <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>}
            {rows && (
              <>
                <div className="mb-3">
                  {toplamEksik === 0
                    ? <Badge color="success-subtle" className="text-success">{t("ceviriler.hepsiTamam")}</Badge>
                    : <span className="text-muted small">{t("ceviriler.toplamEksik", { sayi: sayi(toplamEksik) })}</span>}
                </div>
                <div className="table-responsive">
                  <Table className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("ceviriler.katalog")}</th>
                        <th className="text-end" style={{ width: 110 }}>{t("ceviriler.toplam")}</th>
                        <th className="text-end" style={{ width: 150 }}>{t("ceviriler.eksik")}</th>
                        <th style={{ width: 220 }}>{t("ceviriler.cevrili")}</th>
                        <th style={{ width: 200 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const duzenleme = DUZENLEME[r.katalog] || { not: "kod" };
                        const yuzde = Number(r.ceviriliYuzde ?? 0);
                        return (
                          <tr key={r.katalog}>
                            <td>
                              <div className="fw-semibold">{t(`ceviriler.kataloglar.${r.katalog}`, { defaultValue: r.katalog })}</div>
                              <code className="small text-muted">{r.katalog}</code>
                            </td>
                            <td className="text-end">{sayi(r.toplam)}</td>
                            <td className={`text-end ${Number(r.eksik) > 0 ? "text-danger fw-semibold" : "text-muted"}`}>{sayi(r.eksik)}</td>
                            <td>
                              <Progress
                                value={yuzde}
                                color={yuzde === 100 ? "success" : yuzde >= 50 ? "warning" : "danger"}
                                style={{ height: 8 }}
                              />
                              <div className="small text-muted mt-1">{t("ceviriler.yuzde", { yuzde })}</div>
                            </td>
                            <td className="text-end">
                              {duzenleme.link
                                ? <Link to={duzenleme.link} className="btn btn-sm btn-outline-primary">{t("ceviriler.duzenle")}</Link>
                                : <span className="small text-muted">{t(`ceviriler.notlar.${duzenleme.not}`)}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <div className="text-muted small mt-3">{t("ceviriler.dipnot")}</div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
